import languageService from '../../../services/LanguageService'
import { VersionTag, VersionTagFeature, VersionTagsDefault } from '../../../types/VersionTag'
import { SubgenreMap } from '../../account'
import { TableRowUpdateImpact } from '../types/TableRowUpdateImpact'

export const isFeatureOnlyVersionTagSelection = (versionTags?: VersionTag[]): boolean => {
  if (!versionTags || versionTags.length !== VersionTagFeature.length) {
    return false
  }

  const selectedIds = versionTags.map((tag) => tag.id)
  return VersionTagFeature.every((id) => selectedIds.includes(id))
}

export const hasVisibleFeatureVersions = (updateImpacts: TableRowUpdateImpact[], subgenres?: SubgenreMap): boolean => {
  const subgenreFiltered =
    subgenres && Object.entries(subgenres).length > 0 ? updateImpacts.filter((impact) => subgenres[impact.conventionalSubgenreId]) : updateImpacts

  return subgenreFiltered.some((impact) => {
    if (impact.isFirstVersion || impact.isTooOld) {
      return false
    }

    return impact.updatedFeatures.length > 0 || impact.changedFeatures.length > 0 || impact.versionTags.some((tag) => tag.id === VersionTagFeature[0])
  })
}

export const getNoFeatureFallbackVersionTags = (): VersionTag[] => {
  return languageService.getVersionTags().filter((tag) => VersionTagsDefault.includes(tag.id))
}

export const getVersionTagAutoSelectDatasetKey = (marketIso: string, timeSinceUpdated?: string, parentSubgenres?: SubgenreMap): string => {
  const subgenreKey = Object.entries(parentSubgenres || {})
    .filter(([, selected]) => !!selected)
    .map(([id]) => id)
    .sort()
    .join(',')

  return `${marketIso}|${timeSinceUpdated || ''}|${subgenreKey}`
}

const hasSelectedSubgenres = (subgenres?: SubgenreMap): subgenres is SubgenreMap => {
  return !!subgenres && Object.keys(subgenres).length > 0
}

export const getVersionTagAutoSelectSubgenres = (parentSubgenres?: SubgenreMap, mountUrlSubgenres?: SubgenreMap): SubgenreMap | undefined => {
  if (hasSelectedSubgenres(parentSubgenres)) {
    return parentSubgenres
  }

  return hasSelectedSubgenres(mountUrlSubgenres) ? mountUrlSubgenres : undefined
}

export type VersionTagAutoSelectAction = 'wait' | 'select' | 'remember'

export const getVersionTagAutoSelectAction = ({
  lastEvaluatedDatasetKey,
  datasetKey,
  isReady,
  selectedVersionTags,
  updateImpacts,
  subgenres,
}: {
  lastEvaluatedDatasetKey: string | null
  datasetKey: string
  isReady: boolean
  selectedVersionTags?: VersionTag[]
  updateImpacts: TableRowUpdateImpact[]
  subgenres?: SubgenreMap
}): VersionTagAutoSelectAction => {
  if (lastEvaluatedDatasetKey === datasetKey || !isReady) {
    return 'wait'
  }

  if (shouldAutoSelectDefaultVersionTags(selectedVersionTags, updateImpacts, isReady, subgenres)) {
    return 'select'
  }

  return 'remember'
}

export const shouldAutoSelectDefaultVersionTags = (
  selectedVersionTags: VersionTag[] | undefined,
  updateImpacts: TableRowUpdateImpact[],
  isReady: boolean,
  subgenres?: SubgenreMap
): boolean => {
  if (!isReady) {
    return false
  }

  if (!isFeatureOnlyVersionTagSelection(selectedVersionTags)) {
    return false
  }

  return !hasVisibleFeatureVersions(updateImpacts, subgenres)
}
